import { computed, ref } from 'vue'
import { defineStore } from 'pinia'
import { isAbort } from '@sas/form-runtime'
import { api } from '../api/client'
import type { Entitlements, Feature } from '../api/billing'

export const useBillingStore = defineStore('console-billing', () => {
  const selectedId = ref('')
  const states = ref<Record<string, Entitlements>>({})
  const loading = ref(false)
  const error = ref('')
  const current = computed(() => states.value[selectedId.value] || null)
  let controller: AbortController | undefined
  let generation = 0
  let timer: ReturnType<typeof setTimeout> | undefined

  function schedule(state: Entitlements) {
    clearTimeout(timer)
    const now = new Date(state.server_time).getTime()
    const clockOffset = now - Date.now()
    const boundaries = [state.ends_at, state.current_period_ends_at, ...state.upcoming.map(item => item.starts_at)]
      .filter((date): date is string => Boolean(date)).map(date => new Date(date).getTime()).filter(time => time > now)
    if (!boundaries.length) return
    timer = setTimeout(() => {
      const cached = states.value[state.workspace_id]
      if (cached && state.ends_at && Date.now() + clockOffset >= new Date(state.ends_at).getTime()) {
        states.value[state.workspace_id] = { ...cached, valid: false, features: {}, subscription_status: 'expired', reasons: ['subscription_expired'] }
      }
      void refresh(state.workspace_id)
    }, Math.min(Math.max(Math.min(...boundaries) - now + 100, 100), 2_147_483_647))
  }
  async function refresh(id = selectedId.value) {
    if (!id || id !== selectedId.value) return
    controller?.abort()
    const request = new AbortController(); controller = request
    const currentGeneration = ++generation
    loading.value = true; error.value = ''
    try {
      const state = await api.entitlements(id, request.signal)
      if (currentGeneration !== generation || id !== selectedId.value || state.workspace_id !== id) return
      states.value[id] = state
      schedule(state)
    } catch (e) {
      if (currentGeneration === generation && !isAbort(e)) error.value = (e as Error).message
    } finally { if (currentGeneration === generation) loading.value = false }
  }
  async function select(id: string) {
    controller?.abort(); generation++; clearTimeout(timer)
    selectedId.value = id; loading.value = false; error.value = ''
    // A cached entitlement is refreshed before enabling mutations in a new workspace.
    if (id) { delete states.value[id]; await refresh(id) }
  }
  function permits(id: string, key: 'provider_modes' | 'action_transports' | 'integration_types' | 'action_integration_types', value: string) {
    const state = states.value[id]
    return Boolean(state?.valid && (state.capabilities[key].includes(value) || state.capabilities[key].includes('*')))
  }
  function hasFeature(id: string, feature: Feature): boolean { return Boolean(states.value[id]?.valid && states.value[id]?.features[feature]) }
  function canCreate(id: string): boolean {
    const state = states.value[id]
    return Boolean(state?.valid)
  }
  function reset() { controller?.abort(); generation++; clearTimeout(timer); states.value = {}; selectedId.value = ''; loading.value = false; error.value = '' }
  return { selectedId, states, current, loading, error, refresh, select, hasFeature, canCreate, permits, reset }
})
